// Missing product photos are optional; never hold a PDF until the renderer timeout.
const OPTIONAL_BACKGROUND_WAIT_MS = 3000;

const resourceUrl = (src: string) => new URL(src, document.baseURI).href;
const cancelled = () => new DOMException("PDF visual wait cancelled", "AbortError");

/** Start before PDF content mounts so resource entries remain visible even when the timing buffer fills. */
export const createPdfResourceTracker = () => {
  const completed = new Set<string>();
  const listeners = new Set<(urls: readonly string[]) => void>();
  const cancelWaiters = new Set<() => void>();
  let disposed = false;
  const record = (entries: PerformanceEntryList) => {
    if (disposed) return;
    for (const entry of entries) completed.add(entry.name);
    const urls = entries.map((entry) => entry.name);
    for (const listener of listeners) listener(urls);
  };
  const observer = new PerformanceObserver((list) => record(list.getEntries()));
  observer.observe({ type: "resource", buffered: true });
  record(performance.getEntriesByType("resource"));

  return {
    waitForOptional(sources: string[], signal?: AbortSignal): Promise<void> {
      if (disposed || signal?.aborted) return Promise.reject(cancelled());
      const pending = new Set(sources.map(resourceUrl).filter((url) => !completed.has(url)));
      if (pending.size === 0) return Promise.resolve();

      return new Promise<void>((resolve, reject) => {
        const cleanup = () => {
          clearTimeout(deadline);
          listeners.delete(check);
          cancelWaiters.delete(abort);
          signal?.removeEventListener("abort", abort);
        };
        const finish = () => {
          cleanup();
          resolve();
        };
        const abort = () => {
          cleanup();
          reject(cancelled());
        };
        const check = (urls: readonly string[]) => {
          for (const url of urls) pending.delete(url);
          if (pending.size === 0) finish();
        };
        const deadline = setTimeout(finish, OPTIONAL_BACKGROUND_WAIT_MS);
        listeners.add(check);
        cancelWaiters.add(abort);
        signal?.addEventListener("abort", abort, { once: true });
        if (signal?.aborted) abort();
        else if (pending.size === 0) finish();
      });
    },
    dispose() {
      disposed = true;
      observer.disconnect();
      for (const cancel of cancelWaiters) cancel();
      listeners.clear();
    },
  };
};

export type PdfResourceTracker = ReturnType<typeof createPdfResourceTracker>;

const waitForRequiredBackground = (src: string, signal?: AbortSignal) =>
  new Promise<void>((resolve, reject) => {
    if (signal?.aborted) return reject(cancelled());
    const image = new Image();
    const cleanup = () => {
      image.onload = null;
      image.onerror = null;
      signal?.removeEventListener("abort", abort);
    };
    const abort = () => {
      cleanup();
      reject(cancelled());
    };
    image.onload = () => {
      cleanup();
      resolve();
    };
    image.onerror = () => {
      cleanup();
      reject(new Error("PDF background image failed to load"));
    };
    signal?.addEventListener("abort", abort, { once: true });
    image.src = src;
  });

const waitForImage = async (image: HTMLImageElement, signal?: AbortSignal) => {
  if (signal?.aborted) throw cancelled();
  if (image.decode) {
    if (signal) {
      let abort = () => {};
      const aborted = new Promise<never>((_, reject) => {
        abort = () => reject(cancelled());
        signal.addEventListener("abort", abort, { once: true });
      });
      try {
        await Promise.race([image.decode(), aborted]);
      } finally {
        signal.removeEventListener("abort", abort);
      }
    } else {
      await image.decode();
    }
  } else if (!image.complete) {
    await new Promise<void>((resolve, reject) => {
      const cleanup = () => {
        image.removeEventListener("load", loaded);
        image.removeEventListener("error", failed);
        signal?.removeEventListener("abort", abort);
      };
      const loaded = () => {
        cleanup();
        resolve();
      };
      const failed = () => {
        cleanup();
        reject(new Error("PDF image failed to load"));
      };
      const abort = () => {
        cleanup();
        reject(cancelled());
      };
      image.addEventListener("load", loaded, { once: true });
      image.addEventListener("error", failed, { once: true });
      signal?.addEventListener("abort", abort, { once: true });
    });
  }
  if (signal?.aborted) throw cancelled();
  if (image.naturalWidth === 0) throw new Error("PDF image failed to load");
};

export const waitForPdfImages = async (
  view: HTMLElement,
  tracker: PdfResourceTracker,
  signal?: AbortSignal,
) => {
  const backgroundSources = new Map<string, boolean>();
  for (const element of view.querySelectorAll<HTMLElement>("[data-pdf-background-src]")) {
    const src = element.dataset.pdfBackgroundSrc;
    if (!src) continue;
    const required = element.dataset.pdfBackgroundOptional === undefined;
    backgroundSources.set(src, required || (backgroundSources.get(src) ?? false));
  }

  await Promise.all([
    ...Array.from(view.querySelectorAll("img")).map((image) => waitForImage(image, signal)),
    tracker.waitForOptional(
      Array.from(backgroundSources)
        .filter(([, required]) => !required)
        .map(([src]) => src),
      signal,
    ),
    ...Array.from(backgroundSources)
      .filter(([, required]) => required)
      .map(([src]) => waitForRequiredBackground(src, signal)),
  ]);
};
